import { HubConnection, HubConnectionState } from '@microsoft/signalr'
import { uniq } from 'lodash'
import { useEffect, useRef } from 'react'
import { CertificateDocumentModel } from '../../../shared/models/CertificateDocument.model'

const DOCUMENT_UPDATED_EVENT = 'documentUpdated'
const JOIN_GROUP_METHOD = 'JoinCertificateDocumentGroup'
const LEAVE_GROUP_METHOD = 'LeaveCertificateDocumentGroup'

const isSameDocumentId = (left: string, right: string) => left.toLowerCase() === right.toLowerCase()

export const useCertificateDocumentRealTimeUpdates = (
  connection: HubConnection | undefined,
  certificateDocumentIds: string[],
  onDocumentUpdate: (message: CertificateDocumentModel) => void
) => {
  const documentIds = uniq((certificateDocumentIds || []).filter(Boolean))
  const documentIdsKey = JSON.stringify([...documentIds].sort())

  // Refs keep the long lived hub handlers pointed at the latest values rather than a render snapshot.
  const documentIdsRef = useRef<string[]>(documentIds)
  documentIdsRef.current = documentIds

  const onDocumentUpdateRef = useRef(onDocumentUpdate)
  onDocumentUpdateRef.current = onDocumentUpdate

  // Group membership is transport bookkeeping rather than render state. Holding it in a ref keeps
  // effect cleanups from acting on a stale snapshot and leaving groups the component still needs.
  const joinedGroupIdsRef = useRef<string[]>([])
  const pendingReconciliationRef = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    if (!connection) return

    const handleDocumentUpdated = (message: CertificateDocumentModel) => {
      const updatedDocumentId = message?.id
      if (!updatedDocumentId) return
      if (!documentIdsRef.current.some(id => isSameDocumentId(id, updatedDocumentId))) return

      onDocumentUpdateRef.current(message)
    }

    connection.on(DOCUMENT_UPDATED_EVENT, handleDocumentUpdated)

    return () => {
      connection.off(DOCUMENT_UPDATED_EVENT, handleDocumentUpdated)
    }
  }, [connection])

  useEffect(() => {
    if (!connection) return

    const reconcileGroups = async () => {
      const targetIds = documentIdsRef.current
      const joinedIds = joinedGroupIdsRef.current

      const groupsToJoin = targetIds.filter(
        id => !joinedIds.some(joinedId => isSameDocumentId(joinedId, id))
      )
      const groupsToLeave = joinedIds.filter(
        id => !targetIds.some(targetId => isSameDocumentId(targetId, id))
      )

      if (groupsToJoin.length > 0) {
        try {
          await connection.invoke(JOIN_GROUP_METHOD, groupsToJoin)
          joinedGroupIdsRef.current = uniq([...joinedGroupIdsRef.current, ...groupsToJoin])
        } catch (error) {
          // Left unrecorded on purpose so the next reconciliation retries the join.
        }
      }

      if (groupsToLeave.length > 0) {
        try {
          await connection.invoke(LEAVE_GROUP_METHOD, groupsToLeave)
        } catch (error) {
          // The server drops membership when the connection ends, so a failed leave is harmless.
        } finally {
          joinedGroupIdsRef.current = joinedGroupIdsRef.current.filter(
            id => !groupsToLeave.some(leftId => isSameDocumentId(leftId, id))
          )
        }
      }
    }

    // Serialised so a document id change mid invoke cannot interleave join and leave calls.
    pendingReconciliationRef.current = pendingReconciliationRef.current.then(
      reconcileGroups,
      reconcileGroups
    )
  }, [connection, documentIdsKey])

  useEffect(() => {
    if (!connection) return

    // Group membership is bound to the connection id, so automatic reconnects silently drop it.
    connection.onreconnected(() => {
      joinedGroupIdsRef.current = []

      const rejoinGroups = async () => {
        const targetIds = documentIdsRef.current
        if (targetIds.length === 0) return

        try {
          await connection.invoke(JOIN_GROUP_METHOD, targetIds)
          joinedGroupIdsRef.current = uniq([...joinedGroupIdsRef.current, ...targetIds])
        } catch (error) {
          // Left unrecorded on purpose so the next reconciliation retries the join.
        }
      }

      pendingReconciliationRef.current = pendingReconciliationRef.current.then(
        rejoinGroups,
        rejoinGroups
      )
    })
  }, [connection])

  useEffect(() => {
    return () => {
      const joinedIds = joinedGroupIdsRef.current
      joinedGroupIdsRef.current = []

      if (connection?.state !== HubConnectionState.Connected || joinedIds.length === 0) return

      connection.invoke(LEAVE_GROUP_METHOD, joinedIds).catch(() => {
        // The connection is torn down alongside the component and the server cleans up the groups.
      })
    }
  }, [connection])
}
